import type { TFunction } from 'i18next';
import { useParams, useSearchParams } from 'react-router-dom';

import { type ReactNode, useEffect, useState } from 'react';

import {
  type PendingClaims,
  afterClaimSummary,
  canSaveClaim,
  claimFooter,
  claimLinkPeople,
  claimPicksOf,
  expenseItemsPreview,
  footerRest,
  formatRestAmount,
  isClaimDirty,
  isOpenClaimLink,
  reconcilePendingClaims,
  restLabelKind,
  setClaimUnits,
  shownClaimRows,
  toggleClaim,
  viewerAvailabilities,
} from '@splitfiesta/core';
import {
  ACTUALLY_SUPPORTED_LANGUAGES,
  useNamespace,
  useTranslation,
} from '@splitfiesta/i18n';
import {
  type LinkPickResultDto,
  formatAmountForCurrency,
  formatCurrencyWithCode,
} from '@splitfiesta/shared';

import {
  AfterClaim,
  type AfterClaimProps,
} from '@/components/claimLink/AfterClaim';
import { ClaimList } from '@/components/claimLink/ClaimList';
import { StoppedLink } from '@/components/claimLink/StoppedLink';
import { WhoAreYou } from '@/components/claimLink/WhoAreYou';
import { LanguageSwitch } from '@/components/common/LanguageSwitch';
import type { BadgeStore } from '@/components/common/StoreBadge';
import { Wordmark } from '@/components/common/Wordmark';
import { ThemeToggle } from '@/components/ui/ThemeToggle';

import {
  errorStatus,
  useClaimLinkViewQuery,
  useLinkPickMutation,
} from '@/hooks/queries/useClaimLink';

import { isEmbeddedInMobileApp } from '@/lib/embeddedMode';
import {
  nudgeStoreForPlatform,
  takeClaimInstallCardTurn,
} from '@/lib/nativeAppNudge';

import { handleApiError } from '@/utils/errorUtils';
import { isAndroid, isIOS, isStandalonePWA } from '@/utils/platform';

type Step =
  | { kind: 'who' }
  | { kind: 'list'; userId: string }
  | {
      kind: 'done';
      userId: string;
      done: Omit<AfterClaimProps, 'onChange'> & { open: boolean };
    };

const formatBillDate = (date: string, language: string) => {
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(date);
  const parsed = new Date(dateOnly ? `${date}T00:00:00Z` : date);
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat(language, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    // A date-only bill is a calendar day, not an instant in the viewer's zone.
    ...(dateOnly ? { timeZone: 'UTC' } : {}),
  }).format(parsed);
};

/** The phone's own store, both on a desktop, none inside the app. */
const installStores = (): BadgeStore[] => {
  if (isEmbeddedInMobileApp() || isStandalonePWA()) return [];
  const store =
    nudgeStoreForPlatform() ??
    (isIOS() ? 'ios' : isAndroid() ? 'android' : null);
  return store ? [store] : ['ios', 'android'];
};

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-linear-to-br from-orange-400 to-red-400 p-4 dark:bg-slate-900 dark:bg-none">
      <div className="mx-auto w-full max-w-xl">
        <div className="mb-3 flex items-center justify-between gap-3">
          <Wordmark height={24} />
          <div className="flex items-center gap-3">
            <ThemeToggle variant="auth" />
            <LanguageSwitch variant="auth" />
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

const PANEL = 'rounded-2xl bg-white p-4 shadow-sm dark:bg-gray-800';

/** `/c/:token`: a bill's public pick page, no account. */
export default function ClaimLinkPage() {
  const { token = '' } = useParams();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation('claimLink');
  useNamespace('claimLink');
  useNamespace('expenses');
  useNamespace('errors');

  useEffect(() => {
    const lang = searchParams.get('lang');
    if (
      lang &&
      ACTUALLY_SUPPORTED_LANGUAGES.includes(
        lang as (typeof ACTUALLY_SUPPORTED_LANGUAGES)[number]
      ) &&
      lang !== i18n.language
    ) {
      i18n.changeLanguage(lang);
    }
  }, [searchParams, i18n]);

  const [step, setStep] = useState<Step>({ kind: 'who' });
  const [ended, setEnded] = useState<'stopped' | 'notFound' | null>(null);
  const [pending, setPending] = useState<PendingClaims>({});
  const [error, setError] = useState<string | null>(null);
  const viewQuery = useClaimLinkViewQuery(token, { poll: step.kind === 'who' });
  const pick = useLinkPickMutation(token);
  const view = viewQuery.data;
  const openView = view && isOpenClaimLink(view) ? view : undefined;
  const expense = openView?.expense;

  const viewerId = step.kind === 'who' ? '' : step.userId;
  const items = expense?.lineItems;
  const participants = expense?.lineItemParticipants ?? [];
  const [seenItems, setSeenItems] = useState(items);
  if (items !== seenItems) {
    setSeenItems(items);
    if (viewerId)
      setPending(prev =>
        reconcilePendingClaims(prev, items, viewerId, participants)
      );
  }

  if (step.kind === 'done') {
    const { open, ...done } = step.done;
    return (
      <Shell>
        <AfterClaim
          {...done}
          onChange={
            open
              ? () => {
                  setPending({});
                  setError(null);
                  setStep({ kind: 'list', userId: step.userId });
                }
              : undefined
          }
        />
      </Shell>
    );
  }

  const loadStatus = viewQuery.isError
    ? errorStatus(viewQuery.error)
    : undefined;
  // Cached data outlives a failed GET; a link that is gone must not show it.
  if (loadStatus === 404 || loadStatus === 410) {
    return (
      <Shell>
        <StoppedLink kind="notFound" />
      </Shell>
    );
  }

  // A cached view waits for the mount's own GET.
  const settling = !viewQuery.isFetchedAfterMount && viewQuery.isFetching;
  if (!view || settling) {
    if ((viewQuery.isPending && !viewQuery.isError) || settling) {
      return (
        <Shell>
          <div className={`${PANEL} flex justify-center py-10`}>
            <div className="loading-spinner-dark h-10 w-10" />
          </div>
        </Shell>
      );
    }
    return (
      <Shell>
        <StoppedLink kind="error" onRetry={() => void viewQuery.refetch()} />
      </Shell>
    );
  }

  const payer = view.expense?.payer.name ?? '';
  if (ended === 'notFound') {
    return (
      <Shell>
        <StoppedLink kind="notFound" />
      </Shell>
    );
  }
  if (ended === 'stopped' || !openView || !expense) {
    return (
      <Shell>
        <StoppedLink
          kind="stopped"
          title={view.expense?.title}
          payer={payer || undefined}
        />
      </Shell>
    );
  }

  const people = claimLinkPeople(openView);
  const nameOf = (id: string) => people.find(p => p.userId === id)?.name ?? '';
  // As the app's CurrencyDisplay: the code, since COP, MXN and USD all write "$".
  const billAmount =
    expense.originalAmount != null && expense.originalCurrency
      ? formatCurrencyWithCode(expense.originalAmount, expense.originalCurrency)
      : formatCurrencyWithCode(expense.amount, expense.currency);

  if (step.kind === 'who') {
    return (
      <Shell>
        <div className={PANEL}>
          <WhoAreYou
            title={expense.title}
            payer={payer}
            date={formatBillDate(expense.date, i18n.language || 'en')}
            amount={billAmount}
            items={expense.lineItems.length}
            pickedCount={openView.picked}
            total={openView.total}
            people={people}
            onPick={userId => {
              setPending({});
              setError(null);
              setStep({ kind: 'list', userId });
            }}
          />
        </div>
      </Shell>
    );
  }

  const rowsSource = expense.lineItems;
  const shown = shownClaimRows(rowsSource, pending, viewerId, participants);
  const dirty = isClaimDirty(rowsSource, shown, viewerId, participants);
  const { availabilities } = viewerAvailabilities(
    shown,
    viewerId,
    participants,
    people.map(p => p.userId),
    pending
  );
  const previewResult = expenseItemsPreview(expense, shown);
  const { preview, entryCurrency, foreign, rest } = previewResult;
  const restKind = preview
    ? restLabelKind(preview.restAmount, rowsSource.length)
    : 'hidden';
  const savedShare =
    expense.splits.find(s => s.userId === viewerId)?.amount ?? 0;
  const footer = claimFooter(
    previewResult,
    shown,
    viewerId,
    !dirty && !foreign ? savedShare : undefined
  );
  const groupShare = dirty
    ? preview?.splits.find(s => s.userId === viewerId)?.amount
    : savedShare;
  const person = people.find(p => p.userId === viewerId);
  const canSave = canSaveClaim({
    dirty,
    asked: true,
    viewerPicked: person?.state === 'picked',
  });

  const finish = (response: LinkPickResultDto) => {
    const bill = response.expense;
    const summary = afterClaimSummary(response, viewerId);
    const currency = summary.currency || entryCurrency;
    const money = (amount: number) => formatAmountForCurrency(amount, currency);
    const stores = installStores();
    setStep({
      kind: 'done',
      userId: viewerId,
      done: {
        open: response.status === 'open',
        name: response.people.find(p => p.userId === viewerId)?.name ?? '',
        payer: bill.payer.name,
        total: formatCurrencyWithCode(summary.owes, currency),
        shown: summary.shown.map(({ item, amount }) => ({
          id: item.id,
          name: item.name.trim() || t('expenses:lineItems.unnamed'),
          amount: money(amount),
        })),
        rest: summary.rest
          ? {
              kind: summary.rest.kind,
              amount: formatRestAmount(summary.rest.amount, currency),
            }
          : null,
        more: summary.more,
        picked: summary.picked,
        people: summary.people,
        install:
          stores.length > 0 && takeClaimInstallCardTurn()
            ? {
                stores,
                friends: response.people.filter(
                  p => p.hasApp && p.userId !== viewerId
                ).length,
              }
            : null,
      },
    });
  };

  const save = () => {
    if (!canSave || pick.isPending) return;
    setError(null);
    pick.mutate(
      {
        participantId: viewerId,
        picks: claimPicksOf(shown, viewerId).picks ?? [],
      },
      {
        onSuccess: finish,
        onError: err => {
          const status = errorStatus(err);
          if (status === 410) setEnded('stopped');
          else if (status === 404) setEnded('notFound');
          else
            setError(handleApiError(err, t as unknown as TFunction, 'errors'));
        },
      }
    );
  };

  return (
    <Shell>
      <div className={PANEL}>
        <ClaimList
          name={person?.name ?? ''}
          title={expense.title}
          payer={payer}
          method={expense.unpickedSplit?.method ?? 'equal'}
          rows={shown.map(item => ({
            item,
            availability: availabilities.get(item.id)!,
          }))}
          nameOf={nameOf}
          currency={entryCurrency}
          rest={
            preview && restKind !== 'hidden'
              ? { kind: restKind, mode: rest.mode, amount: preview.restAmount }
              : null
          }
          footer={
            footer
              ? {
                  items: footer.items,
                  itemsAmount: footer.itemsAmount,
                  rest: footerRest(footer),
                }
              : null
          }
          total={
            footer
              ? { amount: footer.total, currency: entryCurrency }
              : { amount: savedShare, currency: expense.currency }
          }
          groupShare={
            foreign && groupShare !== undefined
              ? { amount: groupShare, currency: expense.currency }
              : undefined
          }
          canSave={canSave}
          saving={pick.isPending}
          error={error}
          onToggle={id => {
            setError(null);
            setPending(prev =>
              toggleClaim(prev, id, rowsSource, viewerId, participants)
            );
          }}
          onUnits={(id, units) => {
            setError(null);
            setPending(prev =>
              setClaimUnits(prev, id, units, rowsSource, viewerId, participants)
            );
          }}
          onSave={save}
          onBack={() => {
            setPending({});
            setError(null);
            setStep({ kind: 'who' });
          }}
        />
      </div>
    </Shell>
  );
}
